/**
 * biome_flora.ts — per-biome foliage catalog for the Commons chunk streamer.
 *
 * Maps each of the 6 biomes to its flora prefab keys + a density scalar.
 * `density` is the nominal candidate count per 256×256 m chunk (before the
 * prop-sparse/plant-dense split and the building keep-out filter).
 *
 * INVARIANT: every prefabKey in this file is a real `prefab_model` row — OR is
 * pending registration (the scatter resolves via `prefab_model` and safely SKIPS
 * unregistered keys, so listing an asset before its cook/registration lands is
 * harmless; it starts appearing the moment the row exists).
 * Original 16 keys verified 2026-06-23 via:
 *   spacetime sql atlas -s local "SELECT prefab_key FROM prefab_model WHERE prefab_key='<key>'"
 * The 18 flora-variety keys (3 per biome) were added 2026-07-01 with the Higgsfield
 * generation batch (manifest commons-v1, generate → cook → set_prefab_model).
 */

import type { Biome } from '@shared/terrain.js';

export interface BiomeFlora {
  /** prefab_model rows that scatter in this biome. */
  prefabKeys: string[];
  /**
   * Nominal scatter candidate count per CHUNK_SIZE × CHUNK_SIZE chunk.
   * The chunk scatter loop uses this as `N` before applying the prop-sparse
   * (÷20) or plant-dense (×1) multiplier and the building keep-out filter.
   */
  density: number;
}

export const BIOME_FLORA: Record<Biome, BiomeFlora> = {
  regolith_plains: {
    prefabKeys: [
      'foliage:bristle_grass', 'foliage:ground_vine',
      'foliage:thorn_scrub_bush', 'foliage:silver_plume_grass', 'foliage:regolith_bloom_cactus',
      // 2× variety wave 2026-07-18 (Higgsfield batch 2) — first TREES for this biome.
      'foliage:umbrella_acacia_tree', 'foliage:copperleaf_tree', 'foliage:plains_sage_shrub',
      'foliage:rippleweed_grass', 'foliage:dustbell_flower',
    'foliage:rock_small_a', 'foliage:rock_medium_a', 'foliage:rock_small_b'],
    density: 95,
  },
  crystal_forest: {
    prefabKeys: [
      'foliage:crystal_tree', 'foliage:crystal_flower', 'foliage:crystal_cluster_shrub',
      'foliage:shard_palm_tree', 'foliage:geode_bloom_flower', 'foliage:prism_moss_clump',
      'foliage:prism_birch_tree', 'foliage:chime_willow_tree', 'foliage:quartz_bramble',
      'foliage:luminous_orb_bush', 'foliage:facet_fern', 'foliage:glasspetal_bloom',
    'foliage:rock_small_c', 'foliage:rock_medium_c', 'foliage:rock_small_a'],
    density: 115,
  },
  dune_desert: {
    prefabKeys: [
      'foliage:desert_succulent_a', 'foliage:desert_succulent_b', 'foliage:dune_grass_tuft',
      'foliage:barrel_cactus_alien', 'foliage:dune_palm_tree', 'foliage:sandspire_yucca',
      'foliage:smoke_juniper_tree', 'foliage:mirage_acacia_tree', 'foliage:ember_aloe',
      'foliage:sand_rose_cluster', 'foliage:needle_tumbleweed', 'foliage:sun_urchin_cactus',
    'foliage:rock_small_b', 'foliage:rock_medium_b', 'foliage:rock_large_b'],
    density: 34,
  },
  rocky_highlands: {
    prefabKeys: [
      'foliage:fern_bush', 'foliage:bristle_grass',
      'foliage:highland_pine_tree', 'foliage:cliff_juniper_shrub', 'foliage:boulder_lichen_mound',
      'foliage:storm_cedar_tree', 'foliage:crag_birch_tree', 'foliage:scree_heather',
      'foliage:summit_thistle', 'foliage:ledge_moss_cushion',
    'foliage:rock_large_a', 'foliage:rock_large_c', 'foliage:rock_medium_a', 'foliage:rock_small_a'],
    density: 58,
  },
  moss_wetland: {
    prefabKeys: [
      'foliage:moss_mat', 'foliage:tall_reed', 'foliage:lumen_grass', 'foliage:coral_shrub',
      'foliage:bog_lantern_plant', 'foliage:wetland_mangrove_tree', 'foliage:azure_reed_cluster',
      'foliage:paperbark_swamp_tree', 'foliage:lantern_willow_tree', 'foliage:marsh_helix_reed',
      'foliage:dew_cup_plant', 'foliage:bubble_kelp_mound', 'foliage:mist_orchid', 'foliage:peat_moss_hummock',
    'foliage:rock_small_c', 'foliage:rock_medium_c'],
    density: 125,
  },
  fungal_basin: {
    prefabKeys: [
      'foliage:mushroom_tree', 'foliage:weeping_lumen_tree', 'foliage:glowfern_tree',
      'foliage:giant_puffball_cluster', 'foliage:spore_chimney_fungus', 'foliage:veiled_tendril_tree',
      'foliage:bracket_titan_tree', 'foliage:myco_lantern_tree', 'foliage:shelf_fungus_stack',
      'foliage:spore_veil_grass', 'foliage:ink_cap_cluster', 'foliage:coral_fungus_bloom',
    'foliage:rock_small_c', 'foliage:rock_medium_a'],
    density: 78,
  },
};
